/**
 * useUnreadCounts / useMarkRead — the sidebar's unread counts and the "I
 * opened this" mark (ui-ux-guideline §2.7, 2026-09-17).
 *
 * One endpoint returns the two counts and the ids behind them; the shell
 * polls it once a minute and every list reads the same cache to mark its own
 * unread rows, so a count on the sidebar and a dot on a row can never
 * disagree. Opening a thread or an escalation calls
 * `markRead`, which drops the id from the cache at once (the dot and the
 * badge clear on the click, not on the next poll) and then lets the server
 * confirm.
 *
 * Williams never reads this. An unread message is not noticing material.
 */
import { useCallback } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/services/api';
import type { TrferUnreadCounts, TrferUnreadKind } from '@/types/api';

export const UNREAD_QUERY_KEY = ['trfer-unread'] as const;

export const EMPTY_UNREAD: TrferUnreadCounts = {
  threads: 0,
  families: 0,
  ids: { threads: [], families: [] },
};

/** Which id list a read kind lives in. */
const LIST_OF: Record<TrferUnreadKind, keyof TrferUnreadCounts['ids']> = {
  thread: 'threads',
  escalation: 'families',
};

/** Pure: is this item unread in this payload? */
export function isUnreadIn(counts: TrferUnreadCounts | undefined, kind: TrferUnreadKind, id: string): boolean {
  if (!counts) return false;
  return counts.ids[LIST_OF[kind]].includes(id);
}

/** Pure: the payload with one item read — the count follows the list. */
export function withItemRead(counts: TrferUnreadCounts, kind: TrferUnreadKind, id: string): TrferUnreadCounts {
  const list = LIST_OF[kind];
  if (!counts.ids[list].includes(id)) return counts;
  const nextIds = { ...counts.ids, [list]: counts.ids[list].filter((x) => x !== id) };
  return { ...counts, ids: nextIds, [list]: nextIds[list].length };
}

/**
 * Sort helper for the lists: unread rows first, otherwise the order the rows
 * arrived in (stable). `id` names the field the read kind is keyed on.
 */
export function unreadFirst<T>(rows: T[], isUnread: (row: T) => boolean): T[] {
  return rows
    .map((row, i) => ({ row, i, u: isUnread(row) ? 0 : 1 }))
    .sort((a, b) => a.u - b.u || a.i - b.i)
    .map((x) => x.row);
}

export function useUnreadCounts(options: { enabled?: boolean } = {}) {
  const query = useQuery({
    queryKey: UNREAD_QUERY_KEY,
    queryFn: async () => (await api.trfer.unread()).data,
    refetchInterval: 60_000,
    staleTime: 30_000,
    enabled: options.enabled ?? true,
  });
  const counts = query.data ?? EMPTY_UNREAD;
  const isUnread = useCallback(
    (kind: TrferUnreadKind, id: string) => isUnreadIn(query.data, kind, id),
    [query.data],
  );
  return { ...query, counts, isUnread };
}

export function useMarkRead() {
  const qc = useQueryClient();
  const { mutate } = useMutation({
    mutationFn: ({ kind, itemId }: { kind: TrferUnreadKind; itemId: string }) => api.trfer.markRead(kind, itemId),
    onMutate: ({ kind, itemId }) => {
      qc.setQueryData<TrferUnreadCounts>(UNREAD_QUERY_KEY, (prev) => (prev ? withItemRead(prev, kind, itemId) : prev));
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: UNREAD_QUERY_KEY });
    },
  });
  return useCallback((kind: TrferUnreadKind, itemId: string) => mutate({ kind, itemId }), [mutate]);
}
